import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as EditIcon } from "src/assets/EditIcon.svg";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";

export interface UnsyncedLeagueNameProps {
  /** Current Un-Synced league display name. */
  leagueName: string;
  /** Persist rename (league_id unchanged). */
  onRename: (newName: string) => Promise<void>;
  /** Parent League Settings accordion open — resets dialog when closed. */
  isSettingsOpen?: boolean;
  /** Un-Synced only: close League Settings (same unsaved logic as LEAGUE TYPE X). */
  onCloseSettings?: () => void;
}

/**
 * Un-Synced only: trigger + dialog to rename the local league name.
 * Same dialog chrome as AiSettingsDialog unsaved-changes prompt.
 */
const UnsyncedLeagueName: React.FC<UnsyncedLeagueNameProps> = ({
  leagueName,
  onRename,
  isSettingsOpen = true,
  onCloseSettings,
}) => {
    const [showDialog, setShowDialog] = useState(false);
    const [nameDraft, setNameDraft] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isSettingsOpen) {
            setShowDialog(false);
            setNameDraft("");
        }
    }, [isSettingsOpen]);

    const handleOpenDialog = () => {
        setNameDraft(leagueName || "");
        setShowDialog(true);
    };

    const handleCloseDialog = () => {
        if (isSaving) return;
        setShowDialog(false);
        setNameDraft(leagueName || "");
    };

    const handleNameDraftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Soft cap for UI; same as input maxLength
        setNameDraft(e.target.value.slice(0, 60));
    };

    const handleSave = async () => {
        const next = nameDraft.trim();
        if (!next || next === (leagueName || "").trim()) return;

        try {
            setIsSaving(true);
            await onRename(next);
            setShowDialog(false);
        } catch (err) {
            console.error("Un-Synced rename failed:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const isSaveDisabled =
        isSaving || !nameDraft.trim() || nameDraft.trim() === (leagueName || "").trim();

  return (
    <>
      {/* Same row layout as SettingsSection title + endAdornment (X top-right for Un-Synced) */}
      <Box sx={{ position: "relative", mb: 1, width: "100%", display: "flex", justifyContent: "center" }}>
        <Button
          variant="text"
          startIcon={<EditIcon width={18} height={18} />}
          onClick={handleOpenDialog}
          sx={{
            color: PureLightThemeCaaS.colors.alpha.white[100],
            fontWeight: "bold",
            textTransform: "uppercase",
            textAlign: "center",
            fontFamily: '"Inter", sans-serif',
            fontSize: "15px", // matches SettingsSection title (Typography h6)
            lineHeight: 1.6,
            px: 0.5,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          Change League Name
        </Button>
        {onCloseSettings && (
          <Box sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}>
            <IconButton
              aria-label="close"
              size="small"
              onClick={onCloseSettings}
              sx={{ color: PureLightThemeCaaS.colors.alpha.white[100], ml: 1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </Box>

            <Dialog
                open={showDialog}
                onClose={handleCloseDialog}
                maxWidth="xs"
                fullWidth
                sx={{ zIndex: 1600 }}
                PaperProps={{
                    sx: {
                        bgcolor: "#EDEDED",
                        borderRadius: 1,
                        border: "0.5px solid #000000",
                        boxShadow: "4px 4px 12px rgba(0,0,0,0.25)",
                        p: 1.5,
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            position: "fixed",
                            backdropFilter: "blur(1px) !important",
                            WebkitBackdropFilter: "blur(1px) !important",
                            backgroundColor: "rgba(0, 0, 0, 0.35)",
                        },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        textAlign: "center",
                        color: PureLightThemeCaaS.colors.alpha.black[100],
                        fontWeight: 700,
                        p: 1,
                    }}
                >
                    Change league name
                </DialogTitle>
                <DialogContent sx={{ textAlign: "center", p: 1 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        value={nameDraft}
                        onChange={handleNameDraftChange}
                        inputProps={{ maxLength: 60 }}
                        placeholder="League name"
                        sx={{
                            mt: 0.5,
                            "& .MuiOutlinedInput-root": {
                                bgcolor: "#fff",
                                "& fieldset": { borderColor: "#ccc" },
                                "&:hover fieldset": { borderColor: "#999" },
                                "&.Mui-focused fieldset": {
                                    borderColor: PureLightThemeCaaS.colors.alpha.orange[100],
                                },
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", gap: 1 }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        autoFocus
                        sx={{
                            flex: 1,
                            whiteSpace: "nowrap",
                            p: 0.5,
                            bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
                            "&:hover": { bgcolor: PureLightThemeCaaS.colors.alpha.orange[90] },
                            "&.Mui-disabled": {
                                bgcolor: "rgba(0,0,0,0.12)",
                                color: "rgba(0,0,0,0.26)",
                            },
                        }}
                    >
                        {isSaving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleCloseDialog}
                        disabled={isSaving}
                        sx={{
                            flex: 1,
                            whiteSpace: "nowrap",
                            p: 0.5,
                            borderColor: PureLightThemeCaaS.colors.alpha.black[70],
                            color: PureLightThemeCaaS.colors.alpha.black[100],
                            "&:hover": { borderColor: PureLightThemeCaaS.colors.alpha.black[100] },
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UnsyncedLeagueName;
